import { useRef, useState } from 'react'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import type { IAlert } from 'common/interfaces/IAlert'

import type { WarningProps } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/Warning/warning.types'
import { INFO_CONSTANTS } from 'features/Home/constants/infoConstants'

import { useUpdateSeriesMutation } from '../../api/series.api'
import { isEmrAccessDenied, SERIES_ERROR_CODES, toSeriesApiError } from '../../api/series.transform'
import type { SeriesListRow } from '../../api/series.types'
import { SERIES_PAGES_TEXT } from '../shared/seriesPagesText'

const TOASTS = SERIES_PAGES_TEXT.toasts
const NOTICE_TITLE = 'Series'

type CancelSeries = {
  askCancel: (row: SeriesListRow) => void
  busyId: number | null
  warningProps: WarningProps
}

const notice = (type: ALERT_CONSTANTS, description: string): IAlert => ({
  title: NOTICE_TITLE,
  type,
  description,
})

export const useCancelSeries = (siteId: string): CancelSeries => {
  const [updateSeries] = useUpdateSeriesMutation()
  const { setNotification } = useNotification()
  const [target, setTarget] = useState<SeriesListRow | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const isSendingRef = useRef(false)

  const handleConfirm = async () => {
    if (!target) return
    setTarget(null)
    if (isSendingRef.current) return
    isSendingRef.current = true
    setBusyId(target.id)
    try {
      await updateSeries({ siteId, id: target.id, data: { cancelled: true } }).unwrap()
      setNotification(notice(ALERT_CONSTANTS.SUCCESS, TOASTS.seriesCancelled))
    } catch (error) {
      const failure = toSeriesApiError(error)
      const description = isEmrAccessDenied(failure)
        ? TOASTS.emrAccessDenied
        : failure.code === SERIES_ERROR_CODES.EMR_UNAVAILABLE
        ? TOASTS.cancelEmrNoAnswer
        : failure.message
      setNotification(notice(ALERT_CONSTANTS.ERROR, description))
    } finally {
      isSendingRef.current = false
      setBusyId(null)
    }
  }

  return {
    askCancel: setTarget,
    busyId,
    warningProps: {
      open: target !== null,
      title: TOASTS.cancelTitle,
      warning: TOASTS.cancelWarning,
      okButtonText: INFO_CONSTANTS.YES,
      hasCancel: true,
      handleClose: () => setTarget(null),
      handleConfirm,
    },
  }
}
